import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '@techchange/components';
import axios from 'axios';

import { AUTHENTICATION_STATES } from '../constants/auth';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * Hook to manage the authentication state of the current user.
 *
 * @param {object} preloadedUser  The preloaded user data from SSR, if any.
 * @param {string} preloadedAuthState  The preloaded authentication state from SSR, if any.
 *
 * @returns {object} result
 *
 * @returns {object} result.currentUser     The current user state.
 * @returns {func}   result.setCurrentUser  Callback function to change the current user state.
 *
 * @returns {object} result.error     The error state, if any.
 * @returns {func}   result.setError  Callback function to change the error state.
 *
 * @returns {string} result.authState     The authentication state.
 * @returns {func}   result.setAuthState  Callback function to change the authentication state.
 *
 * @returns {object} result.actions         Set of API callbacks to update the current user state.
 * @returns {func} result.actions.login     Callback function to login the user.
 * @returns {func} result.actions.register  Callback function to register the user.
 * @returns {func} result.actions.logout    Callback function to logout the user.
 * @returns {func} result.actions.update    Callback function to update the user.
 * @returns {func} result.actions.updateMembership  Callback function to update user's memberhsip.
 * @returns {func} result.actions.createAvatar  Callback function to create an avatar for the user.
 */
const useCurrentUser = (preloadedUser, preloadedAuthState) => {

	const [currentUser, setCurrentUser] = useState(preloadedUser);
	const [error, setError] = useState(null);
	const [authState, setAuthState] = useState(
		preloadedAuthState || AUTHENTICATION_STATES.UNINITIALIZED
	);

	const login = useCallback(async (data) => {

		const response = await axiosClient.post(`${API_BASE_URL.USERS}/login`, data);

		setAuthState(AUTHENTICATION_STATES.AUTHENTICATED);

		setCurrentUser((prev) => ({ ...prev, ...response.data }));

		return response;

	}, []);

	const register = useCallback(async (data) => {

		const response = await axiosClient.post(`${API_BASE_URL.USERS}/register`, data);

		setAuthState(AUTHENTICATION_STATES.AUTHENTICATED);

		setCurrentUser({
			avatars: [],
			...response.data,
		});

		return response;

	}, []);

	const logout = useCallback(async () => {

		const response = await axiosClient.post(`${API_BASE_URL.USERS}/logout`);

		setAuthState(AUTHENTICATION_STATES.FAILED);

		setCurrentUser(null);

		return response;

	}, []);

	const update = useCallback(async (data) => {

		const response = await axiosClient.patch(`${API_BASE_URL.USERS}/${currentUser.id}`, data);

		setCurrentUser((prev) => ({ ...prev, ...response.data }));

		return response;

	}, [currentUser]);

	const updateMembership = useCallback(async (id, data) => {

		const response = await axiosClient.patch(`${API_BASE_URL.MEMBERSHIPS}/${id}`, data);

		setCurrentUser((prev) => ({
			...prev,
			memberships: [response.data],
		}));

		return response;

	}, []);

	const createAvatar = useCallback(async (data) => {

		const response = await axiosClient.post(API_BASE_URL.AVATARS, data);

		setCurrentUser((prev) => ({
			...prev,
			avatars: [...prev.avatars, response.data],
		}));

		return response;

	}, []);

	// Fetch the authentication state for the current user on initial load
	useEffect(() => {

		if (authState === AUTHENTICATION_STATES.UNINITIALIZED) {

			setAuthState(AUTHENTICATION_STATES.PENDING);

			axiosClient.get(`${API_BASE_URL.USERS}/login`).then((response) => {

				if (response?.data) {

					setAuthState(AUTHENTICATION_STATES.AUTHENTICATED);

					setCurrentUser(response.data);

				}

			// Catch any errors related to user authentication
			}).catch((err) => {

				console.error(err);

				setError(err);

				setAuthState(AUTHENTICATION_STATES.FAILED);

			});

		}

	}, [authState]);

	return {
		currentUser,
		setCurrentUser,
		error,
		setError,
		authState,
		setAuthState,
		actions: {
			login,
			register,
			logout,
			update,
			updateMembership,
			createAvatar,
		},
	};
};

export default useCurrentUser;
